import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import clsx from 'clsx';
import './SegmentedControl.css';

export type SegmentedTone = 'primary' | 'info' | 'accent' | 'neutral';

export interface SegmentedOption<V extends string | number> {
  value: V;
  label: ReactNode;
  /** Small second line under the label (e.g. unit — "წუთი"). */
  sub?: ReactNode;
}

export interface SegmentedControlProps<V extends string | number> {
  options: ReadonlyArray<SegmentedOption<V>>;
  value: V;
  onChange: (value: V) => void;
  /** Fill color of the checked key. Default 'primary'. */
  tone?: SegmentedTone;
  /** Accessible name for the radiogroup. Required — the control has no visible label of its own. */
  ariaLabel: string;
  disabled?: boolean;
  /** One-shot spring pop on the newly-checked key after a value change. Default true. */
  popOnChange?: boolean;
  className?: string;
}

/**
 * SegmentedControl — a row of chunky extruded "keys", exactly one checked.
 * The checked key wears an honest solid tone fill with a hard under-edge;
 * unchecked keys are calm surface buttons with the same press physics as
 * Button. Radiogroup semantics with roving tabindex + arrow-key movement.
 *
 * The pop animation fires on USER SELECTION only (tap or arrow key) — never
 * on mount, and never when the `value` prop changes from outside (so an
 * optimistic-save revert can't read as a celebration). Keyed by a change
 * counter so re-selecting the same option later re-pops.
 */
export function SegmentedControl<V extends string | number>({
  options,
  value,
  onChange,
  tone = 'primary',
  ariaLabel,
  disabled = false,
  popOnChange = true,
  className,
}: SegmentedControlProps<V>) {
  const groupRef = useRef<HTMLDivElement>(null);
  // Re-pop keying: `pop` is `${value}:${counter}` so the same option can
  // re-pop on a later selection (fresh attribute value restarts the
  // animation when the attribute is re-applied).
  const counter = useRef(0);
  const [pop, setPop] = useState<{ value: V; key: string } | null>(null);

  // Drop a stale pop as soon as the value diverges from it (an external
  // change, e.g. an optimistic-save revert). Without this, a later
  // external change BACK to the popped option would re-apply data-pop and
  // replay the celebration with no user input. (Render-phase adjust —
  // React's derived-state pattern; onAnimationEnd can't be used because
  // reduced-motion sets animation:none and the event never fires.)
  if (pop && pop.value !== value) setPop(null);

  const select = (next: V) => {
    if (disabled || next === value) return;
    if (popOnChange) {
      counter.current += 1;
      setPop({ value: next, key: `${String(next)}:${counter.current}` });
    }
    onChange(next);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    const idx = options.findIndex((o) => o.value === value);
    let next: number | null = null;
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = (idx + 1) % options.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = (idx - 1 + options.length) % options.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = options.length - 1;
        break;
      default:
        return;
    }
    e.preventDefault();
    const option = options[next];
    if (!option) return;
    select(option.value);
    // Move focus with selection (roving tabindex convention).
    const keys = groupRef.current?.querySelectorAll<HTMLButtonElement>('.fluent-segmented__key');
    keys?.[next]?.focus();
  };

  return (
    <div
      ref={groupRef}
      className={clsx('fluent-segmented', className)}
      role="radiogroup"
      aria-label={ariaLabel}
      data-tone={tone}
      data-disabled={disabled || undefined}
      style={{ '--fluent-segmented-count': options.length } as React.CSSProperties}
      onKeyDown={onKeyDown}
    >
      {options.map((o) => {
        const checked = o.value === value;
        return (
          <button
            key={String(o.value)}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={checked ? 0 : -1}
            disabled={disabled}
            className="fluent-segmented__key"
            data-pop={checked && pop?.value === o.value ? pop.key : undefined}
            onClick={() => select(o.value)}
          >
            <span className="fluent-segmented__label">{o.label}</span>
            {o.sub != null && <span className="fluent-segmented__sub">{o.sub}</span>}
          </button>
        );
      })}
    </div>
  );
}
